import { IconInfoCircle } from '@tabler/icons-react'
import type { ReactNode } from 'react'

import { AstraBadge } from '../AstraBadge/AstraBadge'
import { type AllowedIcon, AstraIcon } from '../AstraIcon/AstraIcon'
import { AstraTooltip } from '../AstraTooltip/AstraTooltip'
import { AstraCheckbox } from '../Inputs/AstraCheckbox/AstraCheckbox'
import { KeyboardShortcutPreview } from '../KeyboardShortcutPreview/KeyboardShortcutPreview'
import { AstraParagraph } from '../Typography/AstraParagraph/AstraParagraph'
import { astraTwMerge } from '../Utilities/astraTwMerge'
import type { MenuItemAction, MenuItemCheckbox, MenuItemLink } from './AstraDropdownItem'

type Props = {
    option: MenuItemAction | MenuItemLink | MenuItemCheckbox
    disabled: boolean
}

/** An icon beside a row's title, at its natural size so the title is what truncates. */
export const DropdownItemIcon = ({ icon, disabled = false }: { icon: AllowedIcon; disabled?: boolean }) => (
    <span className={astraTwMerge('shrink-0', disabled && 'text-disabled')}>
        <AstraIcon icon={icon} />
    </span>
)

/** A row's label, which takes the width the trailing elements leave and truncates first. */
export const DropdownItemTitle = ({ children }: { children: ReactNode }) => (
    <AstraParagraph className="min-w-0 flex-1 truncate font-medium text-inherit">{children}</AstraParagraph>
)

/**
 * The trailing elements keep the order the design gives them — text, shortcut, badge, icon — so a row
 * carrying several of them still lines up with its neighbours. Only the title takes the leftover width;
 * everything after it holds its natural size and the title truncates first.
 */
export function DropdownItemContent({ option, disabled }: Props) {
    const { icon: badgeIcon, ...badgeProps } = option.badge ?? {}

    return (
        <>
            {option.type === 'checkbox' && (
                /* The row is the control: it carries the menuitemcheckbox role and aria-checked. The box
                   is a picture of that state, so the subtree is inert — otherwise it is a second, focusable
                   checkbox nested inside a widget. Inert also passes the click through to the row, which
                   is what keeps a click on the box itself from toggling twice. */
                <span inert className="shrink-0">
                    <AstraCheckbox size="small" checked={option.checked} disabled={disabled} />
                </span>
            )}
            {option.icon && <DropdownItemIcon icon={option.icon} disabled={disabled} />}
            <DropdownItemTitle>{option.title}</DropdownItemTitle>
            {option.trailingText && (
                <AstraParagraph as="span" variant="body-small" weight="medium" className="shrink-0 text-subtle">
                    {option.trailingText}
                </AstraParagraph>
            )}
            {'shortcut' in option && option.shortcut && (
                <span className="shrink-0">
                    <KeyboardShortcutPreview shortcut={option.shortcut} />
                </span>
            )}
            {option.badge && (
                <span className="shrink-0">
                    <AstraBadge {...badgeProps} icon={badgeIcon && <AstraIcon icon={badgeIcon} />} size="small" />
                </span>
            )}
            {disabled && option.disabledInfo && (
                <AstraTooltip tooltipContent={option.disabledInfo}>
                    <span className="text-default">
                        <AstraIcon icon={IconInfoCircle} />
                    </span>
                </AstraTooltip>
            )}
            {option.iconRight && <DropdownItemIcon icon={option.iconRight} disabled={disabled} />}
        </>
    )
}
